import css from "./SampleBrowser.sass?inline"
import {DefaultObservableValue, Lifecycle, Nullable} from "@opendaw/lib-std"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {CustomSampleAPI, OpenSampleAPI, SampleStorage} from "@opendaw/studio-core"
import {StudioService} from "@/service/StudioService.ts"
import {SampleView} from "@/ui/browse/SampleView"
import {AssetLocation} from "@/ui/browse/AssetLocation"
import {HTMLSelection} from "@/ui/HTMLSelection"
import {SampleSelection} from "@/ui/browse/SampleSelection"
import {NumberInput} from "@/ui/components/NumberInput"
import {ResourceBrowser} from "@/ui/browse/ResourceBrowser"
import {Sample} from "@opendaw/studio-adapters"
import {FilterGroup, ResourceBrowserConfig} from "@/ui/browse/ResourceBrowserConfig"

const className = Html.adoptStyleSheet(css, "Samples")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
    background?: boolean
    fontSize?: string // em
}

const location = new DefaultObservableValue(AssetLocation.OpenDAW)

type SampleType = "drums" | "bass" | "vocal" | "chords" | "synth" | "melody" | "fx" | "other"
const typeFilter = new DefaultObservableValue<Nullable<SampleType>>(null)
const typeFilterGroup: FilterGroup<Sample, Nullable<SampleType>> = {
    model: typeFilter,
    predicate: (sample, value) => value === null || sample.type === value,
    elements: [
        {value: null, element: <span>All</span>},
        {value: "drums", element: <span>Drums</span>},
        {value: "bass", element: <span>Bass</span>},
        {value: "vocal", element: <span>Vocal</span>},
        {value: "chords", element: <span>Chords</span>},
        {value: "synth", element: <span>Synth</span>},
        {value: "melody", element: <span>Melody</span>},
        {value: "fx", element: <span>FX</span>},
        {value: "other", element: <span>Other</span>}
    ]
}


export const SampleBrowser = ({lifecycle, service, background, fontSize}: Construct) => {
    const linearVolume = service.samplePlayback.linearVolume
    const config: ResourceBrowserConfig<Sample> = {
        name: "samples",
        cloudOnly: true,
        headers: [
            {label: "Name"},
            {label: "Bpm", align: "right"},
            {label: "Sec", align: "right"}
        ],
        fetchOnline: () => (import.meta.env.VITE_CUSTOM_SAMPLE_API_URL ? CustomSampleAPI.get() : OpenSampleAPI.get()).all(),
        fetchLocal: () => SampleStorage.get().list(),
        renderEntry: ({lifecycle: entryLifecycle, service: entryService, selection, item, location: loc, refresh}) => (
            <SampleView
                lifecycle={entryLifecycle}
                service={entryService}
                sampleSelection={selection as SampleSelection}
                playback={entryService.samplePlayback}
                sample={item}
                location={loc}
                refresh={refresh}
            />
        ),
        filterGroups: [typeFilterGroup],
        resolveEntryName: (sample: Sample) => sample.name,
        createSelection: (svc: StudioService, htmlSelection: HTMLSelection) => new SampleSelection(svc, htmlSelection),
        importSignal: "import-sample",
        footer: ({lifecycle: footerLifecycle}) => (
            <div className="footer">
                <label>Volume:</label>
                <NumberInput lifecycle={footerLifecycle} maxChars={3} step={1} model={linearVolume}/>
                <label>dB</label>
            </div>
        ),
        onReload: () => service.samplePlayback.eject(),
        onTerminate: () => service.samplePlayback.eject()
    }
    return (
        <ResourceBrowser
            lifecycle={lifecycle}
            service={service}
            config={config}
            className={className}
            background={background}
            fontSize={fontSize}
            location={location}
        />
    )
}